import { useCallback, useRef, useState } from "react";
import { Box, Flex, Spinner, VStack, useToast } from "@chakra-ui/react";
import type {
  CampaignIntent,
  DisplayMarketingCampaign,
} from "@common/interfaces/KYDMarketing";
import type {
  FacebookPage,
  InstagramAccount,
  Step,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import actions from "src/actions";
import AdSetCards from "./AdSetCards";
import CampaignDetailWizard from "./CampaignDetailWizard";
import PerformanceInsights from "./PerformanceInsights";

export type RightPanelWizardStep = "none" | "edit_campaign" | "edit_adset";

const GRID_BG =
  "linear-gradient(rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.08) 1px, transparent 1px)";

export interface RightPanelProps {
  campaign: DisplayMarketingCampaign;
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  wizardStep: RightPanelWizardStep;
  editingAdsetId: string | null;
  /** When set with initialWizardStep "edit_creative", wizard opens on this ad's creative form. */
  editingAdId?: string | null;
  wizardReady: boolean;
  onEditCampaign: () => void;
  onEditAdset: (adsetId: string) => void;
  /** Open the wizard on the ad creative form for this ad (advanced flow). */
  onEditAd?: (
    ad: import("@common/interfaces/KYDMarketing").DisplayMarketingAd
  ) => void;
  onWizardReady: () => void;
  onCloseWizard: () => void;
  onRefetch: () => void;
  /** Refetch without loading state (e.g. after intent apply/ignore). */
  onRefetchSilent: () => void | Promise<void>;
  onPauseCampaign: () => void;
  onPublishCampaign: (p: { id: string }) => void;
  onUnpublishCampaign: (p: { id: string }) => void;
  onPublishAdset: (p: { campaign_id: string; adset_id: string }) => void;
  onPublishAd?: (p: {
    campaign_id: string;
    adset_id: string;
    ad_id: string;
  }) => void | Promise<void>;
  onUnpublishAdset: (p: { campaign_id: string; adset_id: string }) => void;
  onUnpublishAd: (p: {
    campaign_id: string;
    adset_id: string;
    ad_id: string;
  }) => void | Promise<void>;
  /** Campaign-level activate action loading state. */
  isPublishingCampaign?: boolean;
  /** Composite key "adsetId:adId" for the ad currently being activated. */
  activatingAdKey?: string | null;
  /** Ad set id currently being paused (show loading on that ad set's Pause button). */
  pausingAdsetId?: string | null;
  /** Ad set id currently being activated (show loading on that ad set's Activate button). */
  publishingAdsetId?: string | null;
  /** Composite key "adsetId:adId" for the ad currently being paused. */
  pausingAdKey?: string | null;
  /** Open the ads manager wizard on the review step for the given ad set. */
  onOpenWizardReview?: (adsetId: string) => void;
  initialWizardStep?: Step | null;
  onInitialStepConsumed?: () => void;
}

const getAllIntentsInCampaign = (campaign: DisplayMarketingCampaign) => {
  const campaignIntents = campaign.intents ?? [];
  const adsets = campaign.adsets ?? [];
  const adsetIntents = adsets.flatMap((adset) => adset.intents ?? []);
  const adIntents = adsets.flatMap((adset) =>
    (adset.ads ?? []).flatMap((ad) => ad.intents ?? [])
  );
  return [...campaignIntents, ...adsetIntents, ...adIntents];
};

export default function RightPanel({
  campaign,
  pages,
  instagramAccounts,
  wizardStep,
  editingAdsetId,
  editingAdId,
  wizardReady,
  onEditCampaign,
  onEditAdset,
  onEditAd,
  onWizardReady,
  onCloseWizard,
  onRefetch,
  onRefetchSilent,
  onPauseCampaign,
  onPublishCampaign,
  onUnpublishCampaign,
  onPublishAdset,
  onPublishAd,
  onUnpublishAdset,
  onUnpublishAd,
  isPublishingCampaign,
  activatingAdKey,
  pausingAdsetId,
  publishingAdsetId,
  pausingAdKey,
  onOpenWizardReview,
  initialWizardStep,
  onInitialStepConsumed,
}: RightPanelProps) {
  const isWizardOpen = wizardStep !== "none";
  const showWizard = isWizardOpen && wizardReady;

  const adsetCardRefs = useRef<Record<string, HTMLElement | null>>({});
  const setAdsetCardRef = useCallback(
    (adsetId: string, el: HTMLElement | null) => {
      if (el) adsetCardRefs.current[adsetId] = el;
      else delete adsetCardRefs.current[adsetId];
    },
    []
  );
  const scrollToAdset = useCallback((adsetId: string) => {
    adsetCardRefs.current[adsetId]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "center",
    });
  }, []);

  const toast = useToast();
  const [applyingIntentId, setApplyingIntentId] = useState<string | null>(null);
  const [ignoringIntentId, setIgnoringIntentId] = useState<string | null>(null);
  const handleApplyIntent = useCallback(
    async (intent: CampaignIntent) => {
      if (!intent.actionable) return;
      const s = intent.status as string | undefined;
      if (s !== "SUGGESTED" && s !== undefined) return;
      setApplyingIntentId(intent.id);
      try {
        await actions.ADMIN.batchApplyCampaignIntents([
          { campaign_id: campaign.id, intent_ids: [intent.id] },
        ]);
        toast({
          title: "Intent applied",
          description: "Recommended action was applied successfully.",
          status: "success",
          isClosable: true,
        });
        await onRefetchSilent();
      } catch (err: unknown) {
        toast({
          title: "Failed to apply",
          description:
            (err as Error)?.message ??
            "Could not apply the recommended action.",
          status: "error",
          isClosable: true,
        });
      } finally {
        setApplyingIntentId(null);
      }
    },
    [campaign.id, onRefetchSilent, toast]
  );
  const handleIgnoreIntent = useCallback(
    async (intent: CampaignIntent) => {
      setIgnoringIntentId(intent.id);
      try {
        await actions.ADMIN.ignoreCampaignIntent(campaign.id, [intent]);
        const label =
          intent.explanation?.short_summary ??
          intent.explanation?.summary ??
          intent.actions?.[0]?.label ??
          intent.reason ??
          "Insight";
        toast({
          title: "Insight dismissed",
          description: label.length > 80 ? `${label.slice(0, 77)}…` : label,
          status: "success",
          isClosable: true,
        });
        await onRefetchSilent();
      } catch (err: unknown) {
        toast({
          title: "Could not ignore",
          description:
            (err as Error)?.message ?? "Intent could not be saved as ignored.",
          status: "warning",
          isClosable: true,
        });
      } finally {
        setIgnoringIntentId(null);
      }
    },
    [campaign.id, onRefetchSilent, toast]
  );

  return (
    <Box
      flex="1"
      minW={0}
      overflowY="auto"
      display="flex"
      flexDirection="column"
      position="relative"
      bg="#C9C9C9"
      backgroundImage={!showWizard ? GRID_BG : undefined}
      backgroundSize="48px 48px"
      backgroundPosition="8px 24px"
    >
      {!showWizard && (
        <VStack align="stretch" spacing={5} p={4} flex="1">
          <PerformanceInsights
            campaignId={campaign.id}
            intents={getAllIntentsInCampaign(campaign)}
            campaign={campaign}
            onApplied={onRefetchSilent}
            onScrollToAdset={scrollToAdset}
          />
          <AdSetCards
            campaign={campaign}
            pages={pages}
            instagramAccounts={instagramAccounts}
            onEditCampaign={onEditCampaign}
            onEditAdset={onEditAdset}
            onEditAd={onEditAd}
            onPauseCampaign={onPauseCampaign}
            onPublishCampaign={onPublishCampaign}
            onUnpublishCampaign={onUnpublishCampaign}
            onPublishAdset={onPublishAdset}
            onPublishAd={onPublishAd}
            onUnpublishAdset={onUnpublishAdset}
            onUnpublishAd={onUnpublishAd}
            isPublishingCampaign={isPublishingCampaign}
            activatingAdKey={activatingAdKey}
            pausingAdsetId={pausingAdsetId}
            publishingAdsetId={publishingAdsetId}
            pausingAdKey={pausingAdKey}
            onRefetch={onRefetch}
            onApplyIntent={handleApplyIntent}
            onIgnoreIntent={handleIgnoreIntent}
            applyingIntentId={applyingIntentId}
            ignoringIntentId={ignoringIntentId}
            onOpenWizardReview={onOpenWizardReview}
            registerCardRef={setAdsetCardRef}
          />
        </VStack>
      )}

      {isWizardOpen && !wizardReady && (
        <Flex
          position="absolute"
          inset={0}
          bg="whiteAlpha.700"
          align="center"
          justify="center"
          zIndex={1}
        >
          <Spinner size="lg" />
        </Flex>
      )}

      {isWizardOpen && (
        <Box
          flex="1"
          minH={0}
          display="flex"
          flexDirection="column"
          visibility={wizardReady ? "visible" : "hidden"}
          position={wizardReady ? "relative" : "absolute"}
          left={wizardReady ? undefined : "-9999px"}
          w="full"
        >
          <CampaignDetailWizard
            campaign={campaign}
            step={wizardStep}
            editingAdsetId={editingAdsetId}
            editingAdId={editingAdId ?? undefined}
            onClose={onCloseWizard}
            onSaved={async () => {
              await onRefetch();
              onCloseWizard();
            }}
            onReady={onWizardReady}
            initialWizardStep={initialWizardStep ?? undefined}
            onInitialStepConsumed={onInitialStepConsumed}
          />
        </Box>
      )}
    </Box>
  );
}
